import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const sharedShell = ({ title, subtitle, updated, children }) => (
  <div className="min-h-screen bg-[#0A0A0F] text-[#F0EDE8]">
    <header className="border-b border-[#2A2A35] bg-[#0A0A0F]/80 backdrop-blur sticky top-0 z-30">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
        <Link
          to="/"
          className="flex items-center gap-2 text-[#888] hover:text-[#F0EDE8] text-sm uppercase tracking-wider"
          data-testid="legal-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> Back to CartIQ 360
        </Link>
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#FF6B00] font-bold">
          Legal
        </div>
      </div>
    </header>
    <main className="max-w-3xl mx-auto px-5 sm:px-6 py-10 sm:py-16">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[#FF6B00] font-bold mb-3">
        — {subtitle}
      </div>
      <h1 className="font-display uppercase text-3xl md:text-5xl leading-tight mb-3">
        {title}
      </h1>
      <div className="text-xs text-[#555] uppercase tracking-[0.22em] mb-10">
        Last updated · {updated}
      </div>
      <article className="prose-legal space-y-6 text-[15px] leading-[1.75] text-[#C8C4BF]">
        {children}
      </article>
      <div className="mt-16 pt-8 border-t border-[#2A2A35] flex items-center justify-between">
        <Link
          to="/"
          className="text-[11px] uppercase tracking-[0.22em] text-[#FF6B00] font-bold hover:text-[#F0EDE8]"
        >
          ← Back to home
        </Link>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#555]">
          CartIQ 360 · Solutionist
        </div>
      </div>
    </main>
  </div>
);

const H2 = ({ children }) => (
  <h2 className="font-display uppercase text-xl md:text-2xl text-[#F0EDE8] mt-8 mb-3">
    {children}
  </h2>
);

export function TermsPage() {
  return sharedShell({
    title: "Terms of Service",
    subtitle: "The rules of the road",
    updated: "Apr 2026",
    children: (
      <>
        <p>
          Welcome to CartIQ 360 (&quot;CartIQ&quot;, &quot;we&quot;, &quot;our&quot;).
          By uploading invoices or using any feature of this app, you agree to the
          terms below. If you disagree with any part, please stop using the service.
        </p>
        <H2>1. What CartIQ 360 does</H2>
        <p>
          CartIQ reads PDF invoices that you voluntarily upload from Indian grocery
          and food-delivery apps (Blinkit, Zepto, Swiggy, Zomato, BigBasket etc.) and
          generates personalised spend-intelligence reports. We do not place orders
          on your behalf or transact with any merchant.
        </p>
        <H2>2. Your invoices, your data</H2>
        <p>
          You own every invoice you upload and every report we generate. We only
          read the contents to produce your dashboard; we do not sell, lease or
          syndicate your raw invoice data to third parties. See our Privacy Policy
          for full details.
        </p>
        <H2>3. Account & free tier</H2>
        <p>
          New users get a limited number of free uploads (currently five). Beyond
          that, a paid subscription (Starter, Pro, Family) unlocks unlimited uploads
          and additional features. Paid plans include a 15-day free trial; you can
          cancel before the trial ends at no cost.
        </p>
        <H2>4. Acceptable use</H2>
        <p>
          Please upload only invoices that belong to you (or your family in the case
          of Family plan). Do not attempt to reverse-engineer the service, upload
          malicious files, abuse the WhatsApp interface, or use CartIQ for unlawful
          purposes.
        </p>
        <H2>5. Billing, refunds & cancellation</H2>
        <p>
          Subscriptions are charged via Razorpay on a monthly or annual basis. You
          may cancel anytime from your dashboard; access continues till the end of
          the current billing cycle. Pro-rated refunds are issued only for billing
          errors or platform outages exceeding 72 hours.
        </p>
        <H2>6. Limitation of liability</H2>
        <p>
          Our intelligence reports are informational aids and are not financial
          advice. You remain responsible for your spending decisions. To the maximum
          extent permitted by law, our total liability is capped at the fees paid by
          you in the three months preceding any claim.
        </p>
        <H2>7. Changes to these terms</H2>
        <p>
          We may update these terms to reflect product or legal changes. Material
          changes will be notified via email or WhatsApp where available.
        </p>
        <H2>8. Contact</H2>
        <p>
          Questions? Write to{" "}
          <a href="mailto:support@solutionist.co.in" className="text-[#FF6B00] underline">
            support@solutionist.co.in
          </a>
          .
        </p>
      </>
    ),
  });
}

export function PrivacyPage() {
  return sharedShell({
    title: "Privacy Policy",
    subtitle: "What we collect and why",
    updated: "Apr 2026",
    children: (
      <>
        <p>
          We built CartIQ 360 because Indian families have almost no visibility into
          their fast-growing food bill. This Privacy Policy explains exactly what we
          collect, why, and how we protect it.
        </p>
        <H2>1. Data you give us</H2>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <strong>Invoice PDFs</strong> you upload. We parse these to extract line
            items, quantities, prices, categories and the merchant platform.
          </li>
          <li>
            <strong>Name, email, profile picture</strong> via Google sign-in (only if
            you choose to sign in).
          </li>
          <li>
            <strong>WhatsApp number</strong> (optional) so we can send Saturday
            grocery lists and budget nudges.
          </li>
        </ul>
        <H2>2. Data we derive</H2>
        <p>
          From your invoices we compute category breakdowns, monthly trends, top
          items, platform split and budget alerts. These are stored in our database
          alongside the original invoice line items so that each new upload builds a
          cumulative view.
        </p>
        <H2>3. What we don&apos;t do</H2>
        <ul className="list-disc pl-5 space-y-2">
          <li>We don&apos;t sell or share your raw invoice data with advertisers.</li>
          <li>
            We don&apos;t read your Gmail, messages, bank statements or any data
            you have not explicitly uploaded.
          </li>
          <li>
            We never send WhatsApp messages unless you opt in by entering your
            number.
          </li>
        </ul>
        <H2>4. Third-party services</H2>
        <p>
          We rely on a small, vetted set of partners: Google (authentication),
          Razorpay (payments), Meta (WhatsApp Business API delivery), Anthropic
          (Claude, used for invoice parsing). Each receives only the minimum data
          needed to perform its function.
        </p>
        <H2>5. Data retention & deletion</H2>
        <p>
          Your data is retained as long as your account is active. You can delete
          all your data at any time by emailing{" "}
          <a href="mailto:support@solutionist.co.in" className="text-[#FF6B00] underline">
            support@solutionist.co.in
          </a>{" "}
          — we&apos;ll purge invoices, reports and profile records within 7 working
          days.
        </p>
        <H2>6. Security</H2>
        <p>
          All traffic is served over HTTPS. Session cookies are httpOnly and
          SameSite-Lax. Payment details are handled entirely by Razorpay — we never
          see or store your card or UPI credentials.
        </p>
        <H2>7. Changes & contact</H2>
        <p>
          If we materially change this policy, we&apos;ll notify you via email. For
          anything privacy-related, reach out at{" "}
          <a href="mailto:support@solutionist.co.in" className="text-[#FF6B00] underline">
            support@solutionist.co.in
          </a>
          .
        </p>
      </>
    ),
  });
}
